import {
  Frame,
  Navigation,
  Page,
  TopBar,
  Icon,
  Text,
  InlineStack,
  Modal,
  Button,
} from "@shopify/polaris";
import React, { useEffect, useCallback, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { StoreDataContext } from "../../global-contexts/StoreData";
import { GetProfile } from "../../services/store";
import routes from "../../constants/routes";
import {
  NotificationFilledIcon,
  EyeCheckMarkIcon,
} from "@shopify/polaris-icons";
import {
  GetNotifications,
  MarkNotificationRead,
} from "../../services/notification";

export default function PrivateWrapper({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [isSecondaryMenuOpen, setIsSecondaryMenuOpen] = React.useState(false);
  const [notifications, setNotifications] = React.useState([]);
  const [showModal, setShowModal] = React.useState(false);
  const [selectedNotification, setSelectedNotification] = React.useState(null);

  const [mobileNavigationActive, setMobileNavigationActive] =
    React.useState(false);
  const [userMenuActive, setUserMenuActive] = React.useState(false);
  const { store, setStore } = useContext(StoreDataContext);

  const navItems = [];

  routes.forEach((route) => {
    if (route.protected && route.icon) {
      navItems.push({
        label: route.title,
        icon: route.icon,
        badge: route.badge,

        // Route conf
        url: route.path,
        matches: false,

        selected: location.pathname === route.path,
        subNavigationItems: route?.subNavigationItems?.map((subNavItem) => {
          return {
            selected: location.pathname === subNavItem.path,
            label: subNavItem.title,
            icon: subNavItem.icon,
            disabled: subNavItem.disabled,
            badge: subNavItem.badge,

            // Route conf
            matchPaths: [route.path, subNavItem.path],
            url: subNavItem.path,
            matches: true,
          };
        }),
      });
    }
  });
  const fetchNotifications = async () => {
    const { data } = await GetNotifications();
    if (data.result) {
      setNotifications(data.result);
    }
  };

  useEffect(() => {
    // Check Login and redirect to login if not logged in
    if (!localStorage.getItem("token")) {
      navigate("/login");
    } else {
      if (!store) {
        GetProfile()
          .then((res) => {
            if (!res?.data?.result?.data?.store) {
              navigate("/login");
            }
            setStore(res?.data?.result?.data?.store);
            setLoading(false);
            fetchNotifications();
          })
          .catch((err) => {
            console.log(err);
            navigate("/login");
          });
      } else {
        setLoading(false);
      }
    }
  }, []);

  function handleLogout() {
    setStore(undefined);
    localStorage.removeItem("token");
    navigate("/login");
  }

  const userMenuActions = [
    {
      items: [{ content: "Logout", onAction: handleLogout }],
    },
  ];

  const toggleMobileNavigationActive = useCallback(
    () =>
      setMobileNavigationActive(
        (mobileNavigationActive) => !mobileNavigationActive
      ),
    []
  );

  const toggleUserMenuActive = useCallback(
    () => setUserMenuActive((userMenuActive) => !userMenuActive),
    []
  );

  const userMenuMarkup = (
    <TopBar.UserMenu
      actions={userMenuActions}
      name={store?.shopName}
      detail={store?.storeUrl}
      initials={store?.shopName[0]}
      open={userMenuActive}
      onToggle={toggleUserMenuActive}
    />
  );
  const toggleIsSecondaryMenuOpen = useCallback(
    () => setIsSecondaryMenuOpen((isSecondaryMenuOpen) => !isSecondaryMenuOpen),
    []
  );
  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
    setShowModal(true);
  };
  const handleCloseModal = () => {
    handleMarkRead(selectedNotification._id);
    setShowModal(false);
  };

  const handleMarkRead = async (id) => {
    const { data } = await MarkNotificationRead(id);
    if (data.result) {
      fetchNotifications();
    }
  };

  const secondaryMenuMarkup = (
    <TopBar.Menu
      activatorContent={
        <span style={{ position: "relative", display: "inline-block" }}>
          <Icon source={NotificationFilledIcon} />
          {notifications.some((n) => !n.read) && (
            <span
              style={{
                position: "absolute",
                top: "2px",
                right: "2px",
                width: "8px",
                height: "8px",
                backgroundColor: "red",
                borderRadius: "50%",
              }}
            ></span>
          )}
          <Text as="span" visuallyHidden>
            Notifications
          </Text>
        </span>
      }
      open={isSecondaryMenuOpen}
      onOpen={toggleIsSecondaryMenuOpen}
      onClose={toggleIsSecondaryMenuOpen}
      actions={[
        {
          items: notifications.map((n) => {
            return {
              content: (
                <InlineStack align="space-between">
                  <Button
                    onClick={() => {
                      handleNotificationClick(n);
                    }}
                    variant="tertiary"
                  >
                    {n.title}
                  </Button>
                  <div
                    onClick={() => {
                      handleMarkRead(n._id);
                    }}
                  >
                    {!n.read && <Icon source={EyeCheckMarkIcon} />}
                  </div>
                </InlineStack>
              ),
            };
          }),
        },
      ]}
    />
  );
  const topBarMarkup = (
    <TopBar
      showNavigationToggle
      userMenu={userMenuMarkup}
      onNavigationToggle={toggleMobileNavigationActive}
      secondaryMenu={secondaryMenuMarkup}
    />
  );
  const logo = {
    topBarSource: "/giftClub-logo-white.svg",
    width: 100,
    url: "#",
    accessibilityLabel: "Giftclub logo",
  };
  return (
    <>
      {loading ? (
        <div className="loading-container">Loading</div>
      ) : (
        <Frame
          navigation={
            <Navigation
              logoSuffix={
                <div className="nav-header-logo-container">
                  <img
                    src="/giftkart-logo.svg"
                    alt="logo"
                    height={75}
                    width={75}
                  />
                </div>
              }
              location="/"
            >
              <Navigation.Section separator items={navItems} />
            </Navigation>
          }
          topBar={topBarMarkup}
          logo={logo}
          showMobileNavigation={mobileNavigationActive}
        >
          {selectedNotification && (
            <Modal
              open={showModal}
              onClose={handleCloseModal}
              title="Details"
              primaryAction={{
                content: "Mark as Read",
                onAction: handleCloseModal,
              }}
            >
              <Modal.Section>
                <Text variant={"headingMd"}>{selectedNotification.title}</Text>
                <br />
                <Text variant={"bodyMd"}>
                  {selectedNotification.description}
                </Text>
              </Modal.Section>
            </Modal>
          )}
          <Page>{children}</Page>
        </Frame>
      )}
    </>
  );
}
